import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { showToast } from '@/components/ui/Toast';
import { Select } from '@/components/ui/Select';
import { Card, CardContent } from '@/components/ui/Card';
import { setLocale, t } from '@/lib/i18n';
import { Globe } from 'lucide-react';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
];

/**
 * LanguagePanel — select the organization's default UI language. Saving
 * writes to the organization's `default_language` column and immediately
 * reloads the i18n dictionary so the switch is visible without a reload.
 */
export function LanguagePanel() {
  const orgId = useOrgStore(s => s.activeOrgId);
  const orgs = useOrgStore(s => s.orgs);
  const fetchOrgs = useOrgStore(s => s.fetchOrgs);
  const queryClient = useQueryClient();

  const org = orgs.find((o: any) => o.id === orgId) as any;
  const currentLanguage = org?.default_language || 'en';

  const mutation = useMutation({
    mutationFn: async (language: string) => {
      const res = await apiFetch(`/api/orgs/${orgId}/default-language`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ default_language: language }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.error?.message || 'Failed to update language');
      }
      return language;
    },
    onSuccess: async (language) => {
      // Refresh the in-memory org list so org.default_language reflects the new value.
      await fetchOrgs();
      queryClient.invalidateQueries({ queryKey: ['session-bootstrap'] });
      // Swap the i18n dictionary immediately — no reload needed.
      await setLocale(language);
      showToast.success(t('Language updated'));
    },
    onError: (err: any) => showToast.error(err?.message || t('Failed to update language')),
  });

  if (!orgId) {
    return <div className="text-sm text-muted-foreground">No organization selected.</div>;
  }

  return (
    <Card>
      <CardContent className="py-4">
        {/* Title + dropdown on one row; description aligned under the title */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <Globe className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="font-semibold text-foreground">Default Language</div>
              <p className="text-xs text-muted-foreground mt-0.5">
                The UI language for everyone in this organization. Changing it reloads
                the dictionary immediately — no page refresh required.
              </p>
            </div>
          </div>
          <div className="w-56 shrink-0">
            <Select
              options={LANGUAGE_OPTIONS}
              value={currentLanguage}
              onValueChange={(value) => mutation.mutate(value)}
              disabled={mutation.isPending}
              aria-label={t('Default language')}
            />
            {mutation.isPending && (
              <p className="mt-1 text-xs text-muted-foreground text-right">Saving…</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
